import { useCallback, useState } from 'react'
import { emporionToast as toast } from '@admin/toast'
import type { AdminAccount, AdminProfile } from '../../types'
import { DEFAULT_PROFILE } from './constants'
import { useI18n } from '../../i18n'
import { useProfileDataActions } from './profile-manager/useProfileDataActions'
import { useProfileSecurityActions } from './profile-manager/useProfileSecurityActions'
import type { ProfileManagerArgs } from './profile-manager/types'

const useProfileManager = ({ token, authHeaders }: ProfileManagerArgs) => {
  const { t } = useI18n()
  const [profile, setProfile] = useState<AdminProfile>(DEFAULT_PROFILE)
  const [account, setAccount] = useState<AdminAccount | null>(null)

  const ensureAuthenticated = useCallback(() => {
    if (token) return true
    toast.error(t('profile.toast.loginRequired'))
    return false
  }, [t, token])

  const {
    profileLoading,
    hydrateProfile,
    loadProfile,
    updateProfile,
    resetProfile,
  } = useProfileDataActions({
    token,
    authHeaders,
    t,
    setProfile,
    setAccount,
    ensureAuthenticated,
  })

  const {
    changePassword,
    verifyCurrentPassword,
    verifyTwoFactorFactor,
    setupTwoFactor,
    verifyTwoFactor,
    disableTwoFactor,
    regenerateTwoFactorRecoveryCodes,
    acknowledgeTwoFactorRecoveryCodes,
    loadPasskeys,
    registerPasskey,
    removePasskey,
  } = useProfileSecurityActions({
    token,
    authHeaders,
    t,
    setAccount,
    ensureAuthenticated,
  })

  return {
    profile,
    account,
    profileLoading,
    loadProfile,
    hydrateProfile,
    updateProfile,
    changePassword,
    verifyCurrentPassword,
    verifyTwoFactorFactor,
    setupTwoFactor,
    verifyTwoFactor,
    disableTwoFactor,
    regenerateTwoFactorRecoveryCodes,
    acknowledgeTwoFactorRecoveryCodes,
    loadPasskeys,
    registerPasskey,
    removePasskey,
    resetProfile,
  }
}

export default useProfileManager
